/**
 * Known legacy fields with no target, at least on one node type. Padding is kept
 * on flex/grid; the other layout fields are kept there (and on image where
 * supported), but section now only accepts contentWidthID. Entity IDs are not
 * configuration fields. Unrecognized fields are also excluded by the allowlists.
 */
export const LEGACY_BUILDIFY_DROPPED_FIELDS: readonly string[] = [
  'configs',
  'margin',
  'permission',
  'opacity',
  '_id',
  'padding',
  'borderRadius',
  'backgroundColor',
  'height',
  'flexDirection',
  'rowGap',
  'columnGap',
  'justifyContent',
  'alignItems',
  'maxWidth',
];

type NodeMigration = {
  legacyClass: string;
  targetClass: string;
  fields: readonly string[];
  tableViewFields?: readonly string[];
};

// Match nodes/<name>/configuration.ts in @adornis/prosemirror-editor, except
// flex, whose project entity preserves legacy CSS values (design decision 3).
const nodeMigrations: Readonly<Record<string, NodeMigration>> = {
  'node-flex': {
    legacyClass: 'HTMLBaseContainerFlex',
    targetClass: 'BHBuildifyFlexConfiguration',
    fields: [
      'flexDirection',
      'rowGap',
      'columnGap',
      'justifyContent',
      'alignItems',
      'backgroundColor',
      'borderRadius',
      'height',
      'padding',
    ],
  },
  'node-grid': {
    legacyClass: 'HTMLBaseContainerGrid',
    targetClass: 'ProsemirrorGridConfiguration',
    fields: [
      'columns',
      'columnWidths',
      'allowHorizontalOverflow',
      'gridColumnGap',
      'gridRowGap',
      'revertDirection',
      'padding',
      'backgroundColor',
      'borderRadius',
      'height',
      'tableView',
      'hasHorizontalSeperator',
      'hasVerticalSeperator',
      'thickness',
      'colorSeperators',
    ],
    tableViewFields: ['hasHorizontalSeperator', 'hasVerticalSeperator', 'thickness', 'colorSeperators'],
  },
  'node-section': {
    legacyClass: 'HTMLBaseContainerSection',
    targetClass: 'ProsemirrorSectionConfiguration',
    // Legacy maxWidth is CSS, not a content-width entity ID; do not relabel it.
    fields: ['contentWidthID'],
  },
  'node-image': {
    legacyClass: 'HTMLBaseImage',
    targetClass: 'ProsemirrorImageConfiguration',
    fields: [
      'fileID',
      'width',
      'height',
      'viewMode',
      'aspectRatio',
      'borderRadius',
      'href',
      'target',
      'viewDownloadButton',
      'altTag',
    ],
  },
};

// Consume whole tags (including quoted attributes), comments and raw-text
// elements, so markup-looking text within them is never rewritten.
const htmlTokens =
  /<!--[\s\S]*?(?:-->|$)|<(script|style|textarea|title)(?=[\s/>])(?:[^>"']|"[^"]*"|'[^']*')*>[\s\S]*?(?:<\/\1\s*>|$)|<\/?[a-z][a-z0-9-]*(?=[\s/>])(?:[^>"']|"[^"]*"|'[^']*')*>/gi;
const attributes = /\s+([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function decodeAttribute(value: string): string {
  // Decode once: an original literal &quot; is stored as &amp;quot;.
  return value.replace(/&(?:quot|amp|lt|gt|apos|#39|#34);/g, entity => {
    switch (entity) {
      case '&quot;':
      case '&#34;':
        return '"';
      case '&amp;':
        return '&';
      case '&lt;':
        return '<';
      case '&gt;':
        return '>';
      default:
        return "'";
    }
  });
}

function encodeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function getMigration(tag: string): NodeMigration | undefined {
  const name = /^<(node-(?:flex|grid|section|image))(?=[\s/>])/i.exec(tag)?.[1];
  return name ? nodeMigrations[name.toLowerCase()] : undefined;
}

/** Only engine-owned legacy data attributes are markers; theme headings are current HTML. */
export function hasLegacyBuildifyMarkers(html: string): boolean {
  if (!/HTMLBase/i.test(html)) return false;
  for (const [tag] of html.matchAll(htmlTokens)) {
    if (!getMigration(tag)) continue;
    for (const attribute of tag.matchAll(attributes)) {
      if (
        attribute[1]?.toLowerCase() === 'data' &&
        /&quot;_class&quot;\s*:\s*&quot;HTMLBase/.test(attribute[2] ?? attribute[3] ?? '')
      )
        return true;
    }
  }
  return false;
}

/** Pure, DOM-free migration of layout HTML; project node data stays byte-identical. */
export function migrateLegacyBuildifyHtml(html: string): { html: string; unknownClasses: string[] } {
  if (!hasLegacyBuildifyMarkers(html)) return { html, unknownClasses: [] };
  const unknownClasses = new Set<string>();
  const migrated = html.replace(htmlTokens, tag => {
    const migration = getMigration(tag);
    if (!migration) return tag;
    const attrs = [...tag.matchAll(attributes)];
    const dataAttrs = attrs.filter(attribute => attribute[1]?.toLowerCase() === 'data');
    const data = dataAttrs[0];
    const encoded = data?.[2] ?? data?.[3];
    if (!data || encoded === undefined) return tag;
    let legacy: unknown;
    try {
      legacy = JSON.parse(decodeAttribute(encoded));
    } catch {
      // Keep malformed payloads and their marker, allowing the caller to refuse a save.
      return tag;
    }
    if (!isRecord(legacy) || typeof legacy._class !== 'string') return tag;
    if (legacy._class !== migration.legacyClass) {
      unknownClasses.add(legacy._class);
      return tag;
    }
    // An ambiguous, partially migrated element must not get duplicate config attrs.
    if (dataAttrs.length !== 1 || attrs.some(attribute => attribute[1]?.toLowerCase() === 'config')) return tag;
    const config: Record<string, unknown> = { _class: migration.targetClass };
    for (const field of migration.fields) {
      if (Object.hasOwn(legacy, field)) config[field] = legacy[field];
    }
    // The old grid used an optional entity; the new grid uses a flag + flat fields.
    if (migration.tableViewFields && isRecord(legacy.tableView)) {
      config.tableView = true;
      for (const field of migration.tableViewFields) {
        if (Object.hasOwn(legacy.tableView, field)) config[field] = legacy.tableView[field];
      }
    }
    const whitespace = /^\s*/.exec(data[0])?.[0] ?? ' ';
    const replacement = `${whitespace}config="${encodeAttribute(JSON.stringify(config))}"`;
    return tag.slice(0, data.index) + replacement + tag.slice(data.index + data[0].length);
  });
  return { html: migrated, unknownClasses: [...unknownClasses] };
}

/** The project's theme registers tr-h1 through tr-h4 only. */
export function getThemeHeadingLevel(level: number): number {
  return Number.isFinite(level) ? Math.max(1, Math.min(4, Math.trunc(level))) : 1;
}

/** Restore theme headings without interpreting comments, raw text or attribute values as HTML. */
export function migrateHeadingsToThemeTags(html: string): string {
  const tokens = [...html.matchAll(htmlTokens)];
  const replacements = new Map<number, string>();
  const wrappers: number[] = [];
  const rename = (tag: string, level: number) => tag.replace(/^(<\/?)h[1-6]/i, `$1tr-h${getThemeHeadingLevel(level)}`);

  tokens.forEach((token, index) => {
    const tag = token[0];
    if (/^<node-heading(?=[\s/>])/i.test(tag)) {
      wrappers.push(index);
      return;
    }
    if (/^<\/node-heading\s*>/i.test(tag)) {
      const start = wrappers.pop();
      if (start === undefined || wrappers.length) return;
      const wrapper = tokens[start]!;
      const opening = tokens[start + 1];
      const closing = tokens[index - 1];
      const heading = opening && /^<h([1-6])(?=[\s/>])/i.exec(opening[0]);
      // Only unwrap the engine's single-heading shape. Leave malformed wrappers intact.
      if (
        !heading ||
        !opening ||
        !closing ||
        !new RegExp(`^</h${heading[1]}\\s*>$`, 'i').test(closing[0]) ||
        !/^\s*$/.test(html.slice(wrapper.index! + wrapper[0].length, opening.index)) ||
        !/^\s*$/.test(html.slice(closing.index! + closing[0].length, token.index)) ||
        tokens.slice(start + 2, index - 1).some(([inner]) => /^<\/?(?:node-heading|h[1-6])(?=[\s/>])/i.test(inner))
      )
        return;

      const wrapperAttrs = [...wrapper[0].matchAll(attributes)];
      const levelAttr = wrapperAttrs.find(attribute => attribute[1]?.toLowerCase() === 'level');
      const level = levelAttr ? Number(levelAttr[2] ?? levelAttr[3] ?? levelAttr[4]) : Number(heading[1]);
      if (!Number.isInteger(level) || level < 1 || level > 6) return;
      const innerAttrs = new Set([...opening[0].matchAll(attributes)].map(attribute => attribute[1]!.toLowerCase()));
      // Inner attributes win collisions; copy all other wrapper metadata verbatim.
      const extraAttrs = wrapperAttrs
        .filter(attribute => !innerAttrs.has(attribute[1]!.toLowerCase()))
        .map(attribute => attribute[0])
        .join('');
      replacements.set(start, '');
      replacements.set(start + 1, rename(opening[0].replace(/>$/, `${extraAttrs}>`), level));
      replacements.set(index - 1, rename(closing[0], level));
      replacements.set(index, '');
      return;
    }
    const heading = /^<\/?h([1-6])(?=[\s/>])/i.exec(tag);
    if (!wrappers.length && heading) replacements.set(index, rename(tag, Number(heading[1])));
  });

  let index = 0;
  return html.replace(htmlTokens, tag => replacements.get(index++) ?? tag);
}
